// Weather on the map: a wind field interpolated between the farms (arrows and an animated flow), clouds, rain,
// fog, and snow or rain drops moving with the wind. Every layer can be switched on and off.
import { clamp, reduced } from './util.js';
import { THEME } from './content.js';
import { FARMS } from './fleet.js';
import { WX, WXS, STATIONS } from './weather.js';

var GW = 72, G = null, KEY = '', MASK = null, GERMANY = null, VW = 600, VH = 600;

// shared setup from the map module: projection, view box and the outline of Germany as SVG path data
var PROJ = null;
export function initWeatherMap(proj, w, h, outline) { PROJ = proj; VW = w; VH = h; GERMANY = outline; MASK = null; }

// IDW interpolation of the farm weather onto a grid (view-box coordinates)
function grid(h) {
  // farms and the extra town weather points together
  var src = FARMS.map(function (f) { return { f: f, x: WX[f.id].hours[h] }; }).concat(STATIONS.filter(function (p) { return WXS[p.id]; }).map(function (p) { return { f: p, x: WXS[p.id].hours[h] }; }));
  var key = h + '|' + src.map(function (o) { return o.x.v.toFixed(1) + o.x.dir.toFixed(0) + o.x.cloud; }).join(',');
  if (G && KEY === key) return G; KEY = key;
  var gh = Math.round(GW * VH / VW), cell = VW / GW, pts = src.map(function (o) {
    var f = o.f, q = PROJ(f.lon, f.lat), x = o.x, to = (x.dir + 180) * Math.PI / 180;
    return { x: q[0], y: q[1], u: x.v * Math.sin(to), w: -x.v * Math.cos(to), cloud: x.cloud, rain: x.rain || 0, fog: clamp((1500 - x.vis) / 1300, 0, 1), t: x.t, g: x.g };
  });
  var c = new Float32Array(GW * gh * 7);
  for (var j = 0; j < gh; j++) for (var i = 0; i < GW; i++) {
    var px = (i + 0.5) * cell, py = (j + 0.5) * cell, sw = 0, a = [0, 0, 0, 0, 0, 0, 0];
    pts.forEach(function (p) { var d2 = (p.x - px) * (p.x - px) + (p.y - py) * (p.y - py) + 900, ww = 1 / (d2 * d2); sw += ww; a[0] += ww * p.u; a[1] += ww * p.w; a[2] += ww * p.cloud; a[3] += ww * p.rain; a[4] += ww * p.fog; a[5] += ww * p.t; a[6] += ww * p.g; });
    for (var k = 0; k < 7; k++) c[(j * GW + i) * 7 + k] = a[k] / sw;
  }
  G = { c: c, gw: GW, gh: gh, cell: cell };
  return G;
}
function at(g, x, y) { var i = clamp(Math.floor(x / g.cell), 0, g.gw - 1), j = clamp(Math.floor(y / g.cell), 0, g.gh - 1), o = (j * g.gw + i) * 7; return g.c.subarray(o, o + 7); }
function inside(x, y) {
  if (!MASK) {
    var cv = document.createElement('canvas'), ctx = cv.getContext('2d'), p = new Path2D(GERMANY), gh = Math.round(GW * 2 * VH / VW);
    MASK = { w: GW * 2, h: gh, m: new Uint8Array(GW * 2 * gh) };
    for (var j = 0; j < gh; j++) for (var i = 0; i < GW * 2; i++) MASK.m[j * GW * 2 + i] = ctx.isPointInPath(p, (i + 0.5) * VW / (GW * 2), (j + 0.5) * VH / gh) ? 1 : 0;
  }
  var ii = Math.floor(x / VW * MASK.w), jj = Math.floor(y / VH * MASK.h);
  return ii >= 0 && jj >= 0 && ii < MASK.w && jj < MASK.h && MASK.m[jj * MASK.w + ii] === 1;
}
function rampRGB(t) { var s = THEME.wind; t = clamp(t, 0, 1) * (s.length - 1); var i = Math.min(s.length - 2, Math.floor(t)), f = t - i, p = function (h, k) { return parseInt(h.slice(1 + 2 * k, 3 + 2 * k), 16); }; return 'rgb(' + [0, 1, 2].map(function (k) { return Math.round(p(s[i], k) + (p(s[i + 1], k) - p(s[i], k)) * f); }).join(',') + ')'; }

// wind arrows on a regular grid inside Germany: direction the wind blows to, length and colour by speed
export function arrowsSvg(h) {
  var g = grid(h), s = '<g class="wf-warr">', step = 38;
  for (var y = step / 2; y < VH; y += step) for (var x = step / 2; x < VW; x += step) {
    if (!inside(x, y)) continue;
    var v = at(g, x, y), sp = Math.sqrt(v[0] * v[0] + v[1] * v[1]); if (sp < 0.3) continue;
    var L = 7 + Math.min(24, sp * 1.05), ux = v[0] / sp, uy = v[1] / sp, x0 = x - ux * L / 2, y0 = y - uy * L / 2, x1 = x + ux * L / 2, y1 = y + uy * L / 2, hx = -uy * 3.2, hy = ux * 3.2, col = rampRGB(sp / 30);
    s += '<path d="M' + x0.toFixed(1) + ' ' + y0.toFixed(1) + 'L' + x1.toFixed(1) + ' ' + y1.toFixed(1) + 'M' + (x1 - ux * 6 + hx).toFixed(1) + ' ' + (y1 - uy * 6 + hy).toFixed(1) + 'L' + x1.toFixed(1) + ' ' + y1.toFixed(1) + 'L' + (x1 - ux * 6 - hx).toFixed(1) + ' ' + (y1 - uy * 6 - hy).toFixed(1) + '" style="stroke:' + col + '"/>';
  }
  return s + '</g>';
}

// clouds, rain and fog as one picture (alpha only where there is weather)
var SKY = null;
export function skyImage(h, clouds, fog) {
  if (!SKY) SKY = document.createElement('canvas');
  var g = grid(h), w = 150, hh = Math.round(w * VH / VW); SKY.width = w; SKY.height = hh;
  var x = SKY.getContext('2d'), img = x.createImageData(w, hh), d = img.data;
  for (var j = 0; j < hh; j++) for (var i = 0; i < w; i++) {
    var vx = (i + 0.5) * VW / w, vy = (j + 0.5) * VH / hh, v = at(g, vx, vy), n = 0.5 + 0.3 * Math.sin(vx * 0.045 + h * 0.35) * Math.sin(vy * 0.05 - h * 0.21) + 0.2 * Math.sin((vx + vy) * 0.11 + h * 0.5);
    var o = [0, 0, 0, 0];
    if (clouds) {
      over(o, 236, 241, 250, clamp((v[2] - 35) / 65, 0, 1) * clamp(0.25 + 0.9 * n, 0, 1) * 0.5);
      if (v[3] > 0.05) over(o, v[5] < 0.5 ? 235 : 70, v[5] < 0.5 ? 245 : 150, 255, clamp(v[3] / 2.5, 0.15, 1) * clamp(0.3 + n, 0, 1) * 0.6);
    }
    if (fog && v[4] > 0.02) over(o, 205, 214, 228, v[4] * 0.62);
    var k = (j * w + i) * 4; d[k] = o[0]; d[k + 1] = o[1]; d[k + 2] = o[2]; d[k + 3] = Math.round(o[3] * 255);
  }
  x.putImageData(img, 0, 0);
  return SKY.toDataURL();
}

// paint colour c with alpha ca over the pixel o = [r, g, b, a]
function over(o, cr, cg, cb, ca) { var na = ca + o[3] * (1 - ca); if (na <= 0) return; o[0] = (cr * ca + o[0] * o[3] * (1 - ca)) / na; o[1] = (cg * ca + o[1] * o[3] * (1 - ca)) / na; o[2] = (cb * ca + o[2] * o[3] * (1 - ca)) / na; o[3] = na; }

// ---------- animated wind flow (and rain or snow) on a canvas over the map ----------
var FLOW = { cv: null, parts: [], drops: [], raf: 0, on: false, h: 0, rain: false, theme: 'dark', visible: true, io: null };
export function flowLayer(box, h, on, rain, theme) {
  FLOW.h = h; FLOW.on = on && !reduced; FLOW.rain = rain; FLOW.theme = theme;
  if (!FLOW.on) { if (FLOW.cv && FLOW.cv.parentNode) FLOW.cv.parentNode.removeChild(FLOW.cv); stop(); return; }
  if (!FLOW.cv) {
    FLOW.cv = document.createElement('canvas'); FLOW.cv.className = 'wf-flow'; FLOW.cv.setAttribute('aria-hidden', 'true');
    if (typeof IntersectionObserver !== 'undefined') { FLOW.io = new IntersectionObserver(function (es) { FLOW.visible = es[0].isIntersecting; if (FLOW.visible) go(); }, { rootMargin: '80px' }); }
    document.addEventListener('visibilitychange', function () { if (!document.hidden) go(); });
  }
  box.appendChild(FLOW.cv);
  if (FLOW.io) { FLOW.io.disconnect(); FLOW.io.observe(box); }
  size(); go();
}
function size() {
  var r = FLOW.cv.getBoundingClientRect(), dpr = Math.min(2, window.devicePixelRatio || 1);
  FLOW.cv.width = Math.max(10, Math.round(r.width * dpr)); FLOW.cv.height = Math.max(10, Math.round(r.height * dpr));
  if (!FLOW.parts.length) for (var i = 0; i < 700; i++) FLOW.parts.push(spawn({}));
}
function spawn(p) { for (var k = 0; k < 20; k++) { p.x = Math.random() * VW; p.y = Math.random() * VH; if (inside(p.x, p.y)) break; } p.age = Math.floor(Math.random() * 80); return p; }
function stop() { if (FLOW.raf) cancelAnimationFrame(FLOW.raf); FLOW.raf = 0; }
function go() { if (!FLOW.raf && FLOW.on && FLOW.visible && !document.hidden && FLOW.cv && FLOW.cv.isConnected) FLOW.raf = requestAnimationFrame(frame); }
function frame() {
  FLOW.raf = 0; if (!FLOW.on || !FLOW.visible || document.hidden || !FLOW.cv.isConnected) return;
  var cv = FLOW.cv, ctx = cv.getContext('2d'), s = cv.width / VW, g = grid(FLOW.h), dark = FLOW.theme === 'dark';
  if (Math.abs(cv.getBoundingClientRect().width * Math.min(2, window.devicePixelRatio || 1) - cv.width) > 2) size();
  ctx.setTransform(1, 0, 0, 1, 0, 0);
  ctx.globalCompositeOperation = 'destination-out'; ctx.fillStyle = 'rgba(0,0,0,0.09)'; ctx.fillRect(0, 0, cv.width, cv.height);
  ctx.globalCompositeOperation = 'source-over'; ctx.setTransform(s, 0, 0, s, 0, 0);
  ctx.lineWidth = 1.1; ctx.strokeStyle = dark ? 'rgba(255,255,255,0.55)' : 'rgba(13,30,80,0.45)'; ctx.beginPath();
  FLOW.parts.forEach(function (p) {
    var v = at(g, p.x, p.y), nx = p.x + v[0] * 0.07, ny = p.y + v[1] * 0.07;
    if (++p.age > 90 || !inside(nx, ny)) { spawn(p); p.age = 0; return; }
    ctx.moveTo(p.x, p.y); ctx.lineTo(nx, ny); p.x = nx; p.y = ny;
  });
  ctx.stroke();
  // precipitation: drops fall and drift with the wind where the interpolated rain is above 0.1 mm/h
  if (FLOW.rain) {
    if (FLOW.drops.length < 260) for (var k = 0; k < 8; k++) { var x = Math.random() * VW, y = Math.random() * VH, v0 = at(g, x, y); if (v0[3] > 0.1 && inside(x, y) && Math.random() < Math.min(1, v0[3] / 1.5)) FLOW.drops.push({ x: x, y: y, life: 14 + Math.random() * 10, snow: v0[5] < 0.5 }); }
    ctx.beginPath(); ctx.strokeStyle = dark ? 'rgba(150,200,255,0.8)' : 'rgba(30,90,200,0.7)'; ctx.lineWidth = 1;
    FLOW.drops = FLOW.drops.filter(function (d) {
      var v = at(g, d.x, d.y), dx = v[0] * 0.05, dy = d.snow ? 0.6 : 2.4;
      if (d.snow) { ctx.moveTo(d.x + 0.9, d.y); ctx.arc(d.x, d.y, 0.9, 0, 6.3); } else { ctx.moveTo(d.x, d.y); ctx.lineTo(d.x + dx * 1.5, d.y + dy * 1.5); }
      d.x += dx; d.y += dy; return --d.life > 0;
    });
    ctx.stroke();
  } else FLOW.drops = [];
  FLOW.raf = requestAnimationFrame(frame);
}

// short facts for the legend and the farm marks
export function stormy(w) { return w.g >= 25 || w.v >= 20; }
